import { Button } from "@client/components/ui/button";
import { GenerationOptions } from "@client/components/ui/generation-options";
import { trpc } from "@client/lib/trpc-client";
import { OptionsSearchSchema } from "@shared/entities/routes";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/sites/new/options")({
	component: NewSiteOptionsPage,
	validateSearch: OptionsSearchSchema,
});

function NewSiteOptionsPage() {
	const navigate = useNavigate();
	const { url } = Route.useSearch();

	const trpcUtils = trpc.useUtils();

	const createSiteMutation = trpc.sites.create.useMutation({
		onSuccess: () => {
			toast.success("Site added successfully");
		},
		onError: (error) => {
			toast.error(error.message || "Failed to add site");
		},
	});

	const generateMutation = trpc.generation.generate.useMutation({
		onSuccess: (data) => {
			console.log("Generation started:", data);
			toast.success("Generation started successfully!");

			// Invalidate active jobs and recent activities so the dashboard updates immediately
			trpcUtils.cleanupJobs.getActiveJobs.invalidate();
			trpcUtils.sites.getRecentActivities.invalidate();

			// Navigate to the generation status page
			if (data.jobId) {
				navigate({ to: "/generation/$jobId", params: { jobId: data.jobId } });
			}
		},
		onError: (error) => {
			console.error("Generation failed:", error);
			toast.error(`Failed to start generation: ${error.message}`);
		},
	});

	// No longer need polling logic since we redirect to job status page immediately

	const handleCreateSiteOnly = () => {
		// Normalize URL if needed
		let normalizedUrl = url;
		if (
			!normalizedUrl.startsWith("http://") &&
			!normalizedUrl.startsWith("https://")
		) {
			normalizedUrl = `https://${normalizedUrl}`;
		}

		createSiteMutation.mutate(
			{ url: normalizedUrl },
			{
				onSuccess: (data) => {
					navigate({
						to: "/sites/$siteId",
						params: { siteId: data.id },
						search: { jobId: undefined },
					});
				},
			},
		);
	};

	const handleGenerate = (options: {
		maxUrls: number;
		showFullText: boolean;
	}) => {
		// First normalize the URL if needed
		let normalizedUrl = url;
		if (
			!normalizedUrl.startsWith("http://") &&
			!normalizedUrl.startsWith("https://")
		) {
			normalizedUrl = `https://${normalizedUrl}`;
		}

		console.log(`Starting site creation for URL: ${normalizedUrl}`);

		// Create site first
		createSiteMutation.mutate(
			{ url: normalizedUrl },
			{
				onSuccess: (data) => {
					console.log(`Site created with ID: ${data.id}`);

					// Start the generation job
					const generationOptions = {
						url: normalizedUrl,
						options: {
							maxUrls: options.maxUrls,
							showFullText: options.showFullText,
						},
					};

					console.log("Starting generation with options:", generationOptions);
					generateMutation.mutate(generationOptions);
				},
			},
		);
	};

	// Generation state is now handled by the mutation pending state
	const isGenerating = generateMutation.isPending;

	return (
		<div className="container mx-auto max-w-4xl px-4 py-8">
			<Button
				variant="ghost"
				size="sm"
				className="mb-4"
				onClick={() => navigate({ to: "/sites/new" })}
				disabled={isGenerating}
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to URL entry
			</Button>

			<h1 className="mb-6 font-bold text-3xl">Generation Options</h1>

			<div className="space-y-6">
				<p className="text-muted-foreground">
					Configure generation options for <strong>{url}</strong>:
				</p>

				{/* Generation status is now handled on the dedicated job status page */}

				<div className="grid gap-6 md:grid-cols-2">
					<GenerationOptions
						siteUrl={url}
						onGenerate={handleGenerate}
						isGenerating={isGenerating}
						defaultOptions={{
							maxUrls: 50,
							showFullText: true,
						}}
					/>

					<div className="flex flex-col space-y-4">
						<div className="rounded-lg border p-4">
							<h3 className="mb-2 font-medium">About LLMs.txt Generation</h3>
							<div className="space-y-3 text-muted-foreground text-sm">
								<p>
									The LLMs.txt file helps large language models better
									understand and reference your website content.
								</p>
								<p>
									The generation process crawls your website pages and creates a
									structured file with:
								</p>
								<ul className="ml-4 list-disc space-y-1">
									<li>Site information and metadata</li>
									<li>Page titles and URLs</li>
									<li>Content excerpts for context</li>
									<li>Optional full page content for better understanding</li>
								</ul>
							</div>
						</div>

						{!isGenerating && (
							<div className="rounded-lg border p-4">
								<h3 className="mb-2 font-medium">Not ready to generate yet?</h3>
								<p className="mb-4 text-muted-foreground text-sm">
									You can add the site now and generate the LLMs.txt file later.
								</p>
								<Button
									variant="outline"
									onClick={handleCreateSiteOnly}
									disabled={createSiteMutation.isPending}
									className="w-full"
								>
									{createSiteMutation.isPending ? "Adding..." : "Add Site Only"}
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
